import { lazy, Suspense, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, X } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";

const PinPicker = lazy(() => import("@/components/site/PropertyMap").then((m) => ({ default: m.PinPicker })));

export const MapPinField = ({ lat, lng, location, onChange }) => {
  const [busy, setBusy] = useState(false);
  const find = async () => {
    if (!location || location.trim().length < 3) {
      toast.error("Type the property's location first (e.g. Lekki Phase 1, Lagos)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/geocode", { q: location });
      onChange({ lat: data.lat, lng: data.lng });
      toast.success("Pin placed — drag it or click the map to fine-tune");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const has = typeof lat === "number" && typeof lng === "number";
  return (
    <div className="sm:col-span-2" data-testid="admin-map-pin-field">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label className="lab text-gw-ground">Map pin (shows this listing on the website map)</Label>
        <div className="flex gap-2">
          <button type="button" onClick={find} disabled={busy} data-testid="admin-map-find-button"
            className="lab text-[11px] inline-flex items-center gap-2 border border-gw-ground/30 px-3 py-2 hover:bg-gw-tint transition-colors duration-200 disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />} Find from location
          </button>
          {has && (
            <button type="button" onClick={() => onChange({ lat: null, lng: null })} data-testid="admin-map-clear-button"
              className="lab text-[11px] inline-flex items-center gap-2 border border-gw-ground/30 px-3 py-2 text-destructive hover:bg-red-50 transition-colors duration-200">
              <X size={12} /> Remove pin
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gw-ground/60 mt-1 mb-2">
        {has ? `Pinned at ${lat.toFixed(5)}, ${lng.toFixed(5)}` : "No pin yet — click the map or use “Find from location”."}
      </p>
      <Suspense fallback={<div className="h-64 bg-gw-card animate-pulse" />}>
        <PinPicker lat={lat} lng={lng} onPick={onChange} />
      </Suspense>
    </div>
  );
};
